import { adminRoles, mainIconSize, TABLE_PAGE_SIZE_OPTIONS } from '@/constants';
import { useDateFormatter, useOrgAdminRenderGate } from '@/hooks';
import { useAppStore } from '@/stores/app';
import type { InstrumentPublic } from '@/types';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import ErrorMessage from '@components/ErrorMessage';
import { FullLoading } from '@components/Loaders';
import RouteAccessGate from '@components/RouteAccessGate';
import { Button, Tooltip } from '@nautilusbio/nb-design-system';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import Table from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useLoaderData } from '@tanstack/react-router';
import type { ColDef, ICellRendererParams, PaginationChangedEvent } from 'ag-grid-enterprise';
import type { AgGridReact } from 'ag-grid-react';
import clsx from 'clsx';
import { CheckCircle2, Plus, RefreshCw, XCircle } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import '../../styles/components/ag-grid-overrides.css';
import tableStyles from '../../styles/components/table.module.css';
import globalStyles from '../../styles/index.module.css';
import pageStyles from '../../styles/pages/index.module.css';

import { ActionsCellRenderer, InstrumentFormDialog } from './components';
import { useInstrumentPermissions } from './hooks/useInstrumentPermissions';
import { useInstruments } from './hooks/useInstruments';
import { readStoredPageSize, writeStoredPageSize } from './pageSize';

const tableContainerProps = {
  className: clsx(pageStyles.table, globalStyles.scrollbars),
};

export default function InstrumentsPage() {
  // beforeLoad only guards navigation; this catches an in-page org/role switch. Also the single
  // source of truth for the admin-access predicate (issue #396) — no separate inline computation.
  const gate = useOrgAdminRenderGate(adminRoles);

  const { t } = useTranslation('instruments');
  const { t: tGeneral } = useTranslation('general');
  const formatDate = useDateFormatter();

  // Same answer as useRouteProtection's `selectedOrganization ?? selectedOrgStub` selector: that
  // one falls back on the whole org, this one per-field, which coincide because Org.id is required.
  // Selecting the id keeps this from re-rendering on unrelated org field changes.
  const orgId = useAppStore(state => state.selectedOrganization?.id ?? state.selectedOrgStub?.id) ?? '';

  const loaderDataRaw = useLoaderData({ from: '/instruments/' });
  // gate.hasAccess arm for parity with Organizations/Auth: setOptions seeds initialData
  // regardless of `enabled`, so a denied session must not hydrate a cache it can't read.
  const initialData =
    gate.hasAccess && Array.isArray(loaderDataRaw) ? (loaderDataRaw as InstrumentPublic[]) : undefined;

  const { data, isLoading, isPending, error, refetch } = useInstruments(orgId, initialData, gate.hasAccess);
  const rowData: InstrumentPublic[] = data ?? [];

  // Bypasses staleTime on the `denied -> authorized` flip (e.g. an org switch that regains
  // access) so the table doesn't sit on the previous org's already-fresh cache entry.
  useEffect(() => {
    if (orgId && gate.hasAccess) refetch();
  }, [orgId, gate.hasAccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const gridRef = useRef<AgGridReact<InstrumentPublic>>(null);

  const { canCreate } = useInstrumentPermissions();

  const [pageSize, setPageSize] = useState(readStoredPageSize);
  const lastSavedPageSizeRef = useRef(pageSize);

  // AG Grid fires onPaginationChanged on plain page navigation too, so bail unless the size really
  // moved — otherwise every next-page click re-renders the page and rewrites the same value.
  const handlePaginationChanged = useCallback((e: PaginationChangedEvent<InstrumentPublic>) => {
    const nextPageSize = e.api.paginationGetPageSize();
    if (nextPageSize === lastSavedPageSizeRef.current) return;
    lastSavedPageSizeRef.current = nextPageSize;
    setPageSize(nextPageSize);
    writeStoredPageSize(nextPageSize);
  }, []);

  const handleRefetch = useCallback(async () => {
    const toastId = toast.loading(t('refetching'));
    const result = await refetch();
    if (result.error) {
      toast.error(t('refetch_error'), { id: toastId });
    } else {
      toast.success(t('refetch_success'), { id: toastId });
    }
  }, [refetch, t]);

  const colDefs = useMemo<ColDef<InstrumentPublic>[]>(
    () => [
      {
        field: 'name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('name'),
      },
      {
        field: 'serial_num',
        filter: 'agTextColumnFilter',
        headerName: t('col_serial_num'),
        hide: true,
      },
      {
        field: 'model',
        filter: 'agTextColumnFilter',
        hide: true,
        headerName: t('col_model'),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: (params: ICellRendererParams<InstrumentPublic>) =>
          params.value ? (
            <CheckCircle2 aria-label={t('available')} color="var(--color-success)" size={18} />
          ) : (
            <XCircle aria-label={t('unavailable')} color="var(--color-danger)" size={18} />
          ),
        field: 'is_available',
        filter: 'agSetColumnFilter',
        headerName: t('col_is_available'),
        maxWidth: 150,
      },
      {
        field: 'next_pm_at',
        filter: 'agDateColumnFilter',
        headerName: t('col_next_pm_at'),
        valueFormatter: ({ value }: { value: string | null | undefined }) =>
          value ? formatDate(value) : tGeneral('empty'),
      },
      {
        field: 'created_by_full_name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('created_by'),
      },
      {
        field: 'created_at',
        filter: 'agDateColumnFilter',
        headerName: tGeneral('created_at'),
        hide: true,
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      },
      {
        field: 'updated_by_full_name',
        filter: 'agTextColumnFilter',
        headerName: tGeneral('updated_by'),
        hide: true,
      },
      {
        field: 'updated_at',
        filter: 'agDateColumnFilter',
        headerName: tGeneral('updated_at'),
        hide: true,
        valueFormatter: ({ value }: { value: string | null | undefined }) =>
          value ? formatDate(value) : tGeneral('empty'),
      },
      {
        cellClass: tableStyles.tableCellCenter,
        cellRenderer: (params: ICellRendererParams<InstrumentPublic>) =>
          params.data ? <ActionsCellRenderer data={params.data} /> : null,
        filter: false,
        headerName: tGeneral('actions'),
        maxWidth: 110,
        resizable: false,
        sortable: false,
        suppressAutoSize: true,
      },
    ],
    [t, tGeneral, formatDate],
  );

  // POR-485/#374: beforeLoad returns on PENDING and never re-runs, so without this gate the table
  // below painted before an unauthorized session's redirect landed. Below every hook (hook-order rule).
  if (gate.status !== 'authorized') {
    return <RouteAccessGate deniedLabel={t('checking_access')} pendingLabel={t('fetching')} status={gate.status} />;
  }

  return (
    <Stack alignItems="start" className={pageStyles.organizationContainer} fullWidth>
      <Stack
        alignItems="center"
        className={clsx(pageStyles.titleRow, pageStyles.organizationTitle)}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Stack alignItems="center" fullHeight gap={2} justifyContent="center" orientation="horizontal">
          <Text as="div" className={getTextClassName('lg')}>
            {t('title')}
          </Text>

          <Tooltip
            content={t('refetch_tooltip')}
            placement="right-start"
            trigger={
              <Button data-testid="refetch-instruments-btn" onPress={handleRefetch} visual="unstyled">
                <RefreshCw size={16} />
              </Button>
            }
          />
        </Stack>

        {canCreate && (
          <InstrumentFormDialog
            triggerProps={{
              children: (
                <>
                  <Plus size={mainIconSize} />
                  {t('create_instrument')}
                </>
              ),
              size: 'md',
              visual: 'unstyled',
            }}
          />
        )}
      </Stack>

      {(isLoading || isPending) && <FullLoading label={t('fetching')} />}

      <div
        className={clsx(
          pageStyles.organizationList,
          globalStyles.scrollbars,
          (isLoading || isPending) && pageStyles.hide,
          error && pageStyles.errorContainer,
        )}
      >
        {error ? (
          <ErrorMessage error={error} level="error" />
        ) : rowData.length === 0 && !isLoading && !isPending ? (
          <Stack alignItems="center" fullWidth gap={4} justifyContent="center">
            <Text as="p">{canCreate ? t('empty_state') : t('empty_state_read_only')}</Text>
            {canCreate && (
              <InstrumentFormDialog
                triggerProps={{
                  children: (
                    <>
                      <Plus size={mainIconSize} />
                      {t('create_instrument')}
                    </>
                  ),
                  size: 'md',
                  visual: 'unstyled',
                }}
              />
            )}
          </Stack>
        ) : (
          <Table
            aria-label={t('title')}
            columnDefs={colDefs}
            container={tableContainerProps}
            domLayout="autoHeight"
            enableResponsiveResize={false}
            pagination
            paginationPageSize={pageSize}
            paginationPageSizeSelector={TABLE_PAGE_SIZE_OPTIONS}
            resizeDebounceMs={150}
            ref={gridRef}
            rowData={rowData}
            tableId="instruments-table"
            onPaginationChanged={handlePaginationChanged}
            onGridReady={e => sizeColumnsOnFirstVisit(e.api, 'instruments-table')}
            onGridPreDestroyed={e => flushColumnState(e.api, 'instruments-table')}
          />
        )}
      </div>
    </Stack>
  );
}
